
import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { useTranslations } from "use-intl";
import { Transaction } from "@/types/payments";
import TransactionsTable from '@/components/cash/TransactionsTable';
import TransactionFilters from '@/components/cash/TransactionFilters';
import BankAccountMultiSelect from '@/components/cash/BankAccountMultiSelect';
import { TransactionListFooter, TransactionStatusTabs } from '@/components/bank/TransactionListParts';
import { BankAccountSummary } from "@/types/accounts";
import { TransactionFilters as TransactionFiltersType } from "@/types/payments";
import { ServiceRegistry } from "@/services/ServiceRegistry";

interface TransactionsSectionProps {
  accounts: BankAccountSummary[];
  transactions: Transaction[];
  loadingTransactions: boolean;
  transactionsError: string | null;
  selectedAccounts: string[];
  setSelectedAccounts: React.Dispatch<React.SetStateAction<string[]>>;
  selectedTab: string;
  setSelectedTab: React.Dispatch<React.SetStateAction<string>>;
  filters: TransactionFiltersType;
  handleFilterChange: (key: keyof TransactionFiltersType, value: string) => void;
  resetFilters: () => void;
  sortedTransactions: Transaction[];
  handleManualMatch: (transactionId: string) => void;
  handleMarkAs: (transactionId: string) => void;
  sortKey: keyof Transaction | null;
  sortDirection: 'asc' | 'desc';
  handleSort: (key: keyof Transaction) => void;
  renderSortIcon: (key: keyof Transaction) => React.ReactNode;
  formatCurrency: (amount: number, currency: string) => string;
  highlightTransactionId?: string | null;
  page: number;
  pageSize: number;
  total: number;
  setPage: (page: number) => void;
  /** FIN-1021/AC2/AC4 — see TransactionsTable's own doc comment. */
  waitingSettlementLabels?: Record<string, 'waiting' | 'booking' | 'mapped_not_retried' | 'entry_failed'>;
}

const TransactionsSection: React.FC<TransactionsSectionProps> = ({
  accounts,
  transactions,
  loadingTransactions,
  transactionsError,
  selectedAccounts,
  setSelectedAccounts,
  selectedTab,
  setSelectedTab,
  filters,
  handleFilterChange,
  resetFilters,
  sortedTransactions,
  handleManualMatch,
  handleMarkAs,
  sortKey,
  sortDirection,
  handleSort,
  renderSortIcon,
  formatCurrency,
  highlightTransactionId,
  page,
  pageSize,
  total,
  setPage,
  waitingSettlementLabels,
}) => {
  const t = useTranslations('consultantTransactions.page');
  const [showFilters, setShowFilters] = useState(false);
  const [aisEarliestDate, setAisEarliestDate] = useState<string | undefined>();
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  useEffect(() => {
    // Empty selection means "all accounts" — apply most restrictive constraint across all
    const relevantAccounts = selectedAccounts.length > 0
      ? accounts.filter(a => selectedAccounts.includes(a.id))
      : accounts;

    const uniqueBics = [...new Set(
      relevantAccounts.map(a => a.bic).filter((b): b is string => Boolean(b))
    )];

    if (!uniqueBics.length) {
      setAisEarliestDate(undefined);
      return;
    }
    const service = ServiceRegistry.getBankInfoService();
    Promise.all(uniqueBics.map(bic => service.getConstraint(bic))).then(constraints => {
      const earliest = constraints
        .filter(Boolean)
        .flatMap(c => {
          if (!c?.aisMaxDateRangeMonths) return [];
          const d = new Date();
          d.setMonth(d.getMonth() - c.aisMaxDateRangeMonths);
          return [d.toISOString().split('T')[0]];
        })
        .sort()
        .at(-1);
      setAisEarliestDate(earliest);
      // Clamp existing startDate filter if it's before the earliest allowed date
      if (earliest && filters.startDate && filters.startDate < earliest) {
        handleFilterChange('startDate', earliest);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccounts, accounts]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('title')}</CardTitle>
        <CardDescription>
          {t('description')}
        </CardDescription>
        
        {/* Bank Account Filter */}
        <div className="mt-4">
          <BankAccountMultiSelect 
            accounts={accounts}
            selectedAccounts={selectedAccounts}
            setSelectedAccounts={setSelectedAccounts}
          />
        </div>
      </CardHeader>
      <CardContent>
        <TransactionStatusTabs selectedTab={selectedTab} onSelectedTabChange={setSelectedTab} />
        
        <div className="flex justify-end mb-4">
          <TransactionFilters
            filters={filters}
            handleFilterChange={handleFilterChange}
            resetFilters={resetFilters}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            aisEarliestDate={aisEarliestDate}
          />
        </div>
        
        {loadingTransactions ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <span className="ml-2">{t('loading')}</span>
          </div>
        ) : transactionsError ? (
          <div className="text-center p-4 bg-red-50 rounded-lg border border-red-200">
            <p className="text-red-600 mb-2">{transactionsError}</p>
            <button 
              className="px-3 py-1 bg-primary text-white text-sm rounded hover:bg-primary/90"
            >
              {t('tryAgain')}
            </button>
          </div>
        ) : (
          <>
            <TransactionsTable
              transactions={sortedTransactions}
              formatCurrency={formatCurrency}
              handleManualMatch={handleManualMatch}
              handleMarkAs={handleMarkAs}
              sortKey={sortKey}
              sortDirection={sortDirection}
              handleSort={handleSort}
              renderSortIcon={renderSortIcon}
              highlightTransactionId={highlightTransactionId}
              waitingSettlementLabels={waitingSettlementLabels}
            />
            
            <TransactionListFooter
              shownCount={sortedTransactions.length}
              loadedCount={transactions.length}
              total={total}
              selectedAccountCount={selectedAccounts.length}
              selectedTab={selectedTab}
              matchedFilter={filters.matched}
              page={page}
              totalPages={totalPages}
              setPage={setPage}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default TransactionsSection;
